/**
 * HotelModal Component
 * Displays hotel/cruise room upgrade details with pricing table
 * WHY: Reused for both hotel and cruise upgrades (same Room data structure)
 */

import React from "react";
import {
  Stack,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useMediaQuery
} from "@mui/material";
import { Close, RoomOutlined as RoomIcon } from "@mui/icons-material";
import { useTranslations } from "next-intl";
import { ColletteIconButton } from "@collette-travel/collette-common-controls";
import Image from "next/image";
import { RoomUpgrades } from "@/types/dto/pdp/pdp-itinerary";

export interface HotelModalProps {
  room: RoomUpgrades;
  handleClose: () => void;
}

const HotelModal: React.FC<HotelModalProps> = ({
  room,
  handleClose
}) => {
  const t = useTranslations("PDP");

  const isMd = useMediaQuery((theme) => theme.breakpoints.up("md"));

  return (
    <Stack
      sx={{
        borderRadius: "1rem",
        backgroundColor: "background.default",
        position: "relative",
        padding: {xs: "2.4rem", md: "4rem 6rem"},
        gap: "2rem"
        // WHY: Matches Figma modal padding (60px = 6rem, 40px = 4rem)
      }}
    >
      {/* Close Button */}
      <ColletteIconButton
        onClick={handleClose}
        testId="hotel-modal-close-button"

        sx={{
          position: "absolute",
          top: {xs: 8, md: 24},
          right: {xs: 8, md: 24},
          zIndex: 1
        }}
      >
        <Close />
      </ColletteIconButton>

      {/* Top Row: Image + Content */}
      <Stack
        sx={{
          display: "flex",
          gap: "2rem",
          alignItems: "flex-start",
          flexDirection: {xs: "column-reverse", md: "row"}
        }}
      >
        {/* Left Column: Image */}
        {room.imageUrl && (
          <Box
            sx={{
              position: "relative",
              width: {xs: "100%", md: "30rem"},
              height: "20rem",
              borderRadius: "2rem",
              overflow: "hidden",
              flexShrink: 0
              // WHY: Fixed 300px × 200px image from Figma
            }}
          >
            <Image
              src={room.imageUrl}
              alt={room.title}
              fill
              sizes="(max-width: 900px) 100vw, 300px"
              style={{ objectFit: "cover" }}
            />
          </Box>
        )}

        {/* Right Column: Title, Location, Description */}
        <Stack
          gap="1.2rem"
          flex={1}
          // WHY: 12px gap between title/location/description per Figma
        >
          {/* Title */}
          <Typography
            component="h2"
            sx={{
              fontSize: "2rem",
              fontWeight: 600,
              lineHeight: 1.3,
              color: "primary.main",
              margin: 0
              // WHY: H5 Desktop from Figma (20px, semibold, Primary/Blue)
            }}
          >
            {room.title}
          </Typography>

          {/* Location with Icon */}
          {room.location && (
            <Box
              sx={{
                display: "flex",
                gap: "0.8rem",
                alignItems: "center"
                // WHY: 8px gap between icon and text per Figma
              }}
            >
              <RoomIcon sx={{ fontSize: "2.4rem", color: "text.primary" }} />
              <Typography
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 500,
                  lineHeight: 1.5,
                  color: "text.primary",
                  letterSpacing: "0.4px"
                  // WHY: Subtitle 2 from Figma (16px, medium, tracking 0.4px)
                }}
              >
                {room.location}
              </Typography>
            </Box>
          )}

          {/* Description */}
          <Typography
            sx={{
              fontSize: "1.6rem",
              fontWeight: 400,
              lineHeight: 1.6,
              color: "text.primary"
              // WHY: Body 3 Regular from Figma (16px, regular, 1.6 line height)
            }}
          >
            {room.description}
          </Typography>
        </Stack>
      </Stack>

      {/* Bottom Row: Pricing Table */}
      {room.rooms && room.rooms.length > 0 && (
        <Box>
          {/* D/T version */}
          {isMd && (
            <Table
              sx={{
                "& .MuiTableCell-root": {
                  padding: "2.4rem",
                  border: "none",
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                  // WHY: 24px padding, divider border per Figma
                }
              }}
            >
              {/* Header Row */}
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      lineHeight: 1.5,
                      color: "text.primary",
                      letterSpacing: "0.4px",
                      textAlign: "left"
                      // WHY: Bold 16px headers per Figma
                    }}
                  >
                    {t("Description")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      lineHeight: 1.5,
                      color: "text.primary",
                      letterSpacing: "0.4px",
                      width: "10.2rem"
                      // WHY: 102px column width per Figma
                    }}
                  >
                    {t("Room")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      lineHeight: 1.5,
                      color: "text.primary",
                      letterSpacing: "0.4px",
                      width: "10.2rem"
                    }}
                  >
                    {t("Night")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontSize: "1.6rem",
                      fontWeight: 700,
                      lineHeight: 1.5,
                      color: "text.primary",
                      letterSpacing: "0.4px",
                      width: "10.2rem",
                      "&::before": {
                        content: "'*'"
                      }
                    }}
                  >
                    {t("Price")}
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* Data Rows */}
              <TableBody>
                {room.rooms.map((detail, index) => (
                  <TableRow
                    key={`room-detail-${detail.description}-${detail.price}`}
                    sx={{
                      backgroundColor: index % 2 === 1 ? "background.warm_strong" : "background.default"
                      // WHY: Alternating row backgrounds per Figma
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: "text.primary",
                        letterSpacing: "0.4px"
                        // WHY: Medium 16px per Figma
                      }}
                    >
                      {detail.description}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: "text.primary",
                        letterSpacing: "0.4px"
                      }}
                    >
                      {detail.room}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: "text.primary",
                        letterSpacing: "0.4px"
                      }}
                    >
                      {detail.nights}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "1.6rem",
                        fontWeight: 500,
                        lineHeight: 1.5,
                        color: "text.primary",
                        letterSpacing: "0.4px"
                      }}
                    >
                      {detail.price}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {/* Mobile version */}
          {!isMd && (
            <Stack data-testid="mobile-pricing-table">
              {room.rooms.map((detail, index) => (
                <Stack
                  key={`room-detail-${detail.description}-${detail.price}`}
                  sx={{
                    backgroundColor: index % 2 === 1 ? "background.warm_strong" : "background.default",
                    borderTop: (theme) => index % 2 === 1 ? `solid 1px ${theme.palette.divider}` : "none",
                    borderBottom: (theme) => index % 2 === 1 ? `solid 1px ${theme.palette.divider}` : "none",
                    gap: "1.2rem",
                    padding: "2.4rem",
                    fontSize: "1.6rem"
                  }}
                >
                  <Typography fontWeight={700}>
                    {detail.description}
                  </Typography>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography>
                      {t("Room")}:
                    </Typography>
                    <Typography>
                      {detail.room}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography>
                      {t("Night")}:
                    </Typography>
                    <Typography>
                      {detail.nights}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Typography>
                      {t("Price")}:
                    </Typography>
                    <Typography>
                      {detail.price}
                    </Typography>
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
          {/* Footer Text */}
          <Box
            sx={{
              padding: "1.2rem 2.4rem",
              textAlign: "right"
              // WHY: 12px top/bottom padding, 24px sides per Figma
            }}
          >
            <Typography
              sx={{
                fontSize: "1.4rem",
                fontWeight: 400,
                lineHeight: 1.4,
                color: "text.primary"
                // WHY: Small Body 2 from Figma (14px, regular)
              }}
            >
             {t("Total for all passengers")}
            </Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
export default HotelModal;
